import { createRouter, createWebHistory } from 'vue-router'
import { DEFAULT_TITLE, pageTitle } from '@/utils/constants.js';
import { authState, authReady, refreshEmailVerified, clearAuthProfileFlags } from '@/utils/auth';
import { fetchAdminMe } from '@/utils/adminApi';
import { routes } from '@/router/routes.js';
import { isPublicSite, isAppSite, publicUrl, appUrl } from '@/utils/siteContext';

export { routes };

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to, _from, next) => {
    await authReady;

    // Host-based route ownership: the same build/route table is served on both the
    // public site and the application host, so a route's meta decides which host may
    // render it. Routes tagged `hostAgnostic` (ownership genuinely undecided, e.g.
    // /public-person, /public-event) are exempt and left reachable on both hosts
    // unchanged. This runs before the auth/admin checks below so an app-only route
    // reached from the public host redirects to the app host before its own guard
    // even evaluates. Cross-host navigation is a real browser navigation
    // (window.location), not a Vue Router transition, since the target is a different
    // origin.
    if (!to.meta.hostAgnostic) {
        if (isAppSite && to.meta.publicOnly) {
            window.location.href = publicUrl(to.fullPath);
            next(false);
            return;
        }
        if (isPublicSite && !to.meta.public) {
            window.location.href = appUrl(to.fullPath);
            next(false);
            return;
        }
    }

    // Application root is the session-aware app entry point (not the public
    // marketing home page, which only renders on the public host). For now this only
    // resolves to Current Sky or the login page; restoring the member's last active
    // workspace is future work, out of scope here.
    if (isAppSite && to.path === '/') {
        next({ path: authState.isAuthenticated ? '/current-sky' : '/login', replace: true });
        return;
    }

    // A route can be public on the public site while still being normal member
    // application functionality on the app host (e.g. Current Sky) -- the app-host
    // authentication boundary applies regardless of the route's public-site status.
    // A visitor must not be able to browse it merely by entering an app.* URL.
    if (isAppSite && to.meta.requiresAuthOnAppHost && !authState.isAuthenticated) {
        next({ path: '/login', replace: true });
        return;
    }

    if (to.meta.requiresAdmin) {
        if (!authState.isAuthenticated) {
            next({
                name: 'admin-login',
                query: { message: 'Please sign in to continue.' },
                replace: true,
            });
            return;
        }
        try {
            await fetchAdminMe();
        } catch (error) {
            const status = error?.response?.status;
            if (status === 401) {
                authState.isAuthenticated = false;
                clearAuthProfileFlags();
                next({
                    name: 'admin-login',
                    query: { message: 'Please sign in to continue.' },
                    replace: true,
                });
                return;
            }
            next({ name: 'admin-unauthorized', replace: true });
            return;
        }
    }

    if (to.meta.requiresAuth && !authState.isAuthenticated) {
        next({ path: '/', replace: true });
        return;
    }

    if (
        to.meta.requiresVerifiedEmail
        && authState.isAuthenticated
        && !authState.emailVerified
    ) {
        // Refresh in case verification completed in another tab.
        await refreshEmailVerified();
        if (!authState.emailVerified) {
            next({ name: 'verify-email-required', replace: true });
            return;
        }
    }

    if (to.name === 'evolutionary-profile') {
        const title = 'Your Evolutionary Profile | SkySpeaks';
        pageTitle.value = title;
        document.title = title;
    } else {
        pageTitle.value = DEFAULT_TITLE;
        document.title = DEFAULT_TITLE;
    }
    next();
});


export default router
